/**
 * Color ramp definitions for weather data visualization
 * Each stop contains a position (0-1) and RGB color values (0-255)
 */

export interface ColorStop {
  position: number; // 0.0 to 1.0
  color: [number, number, number]; // RGB 0-255
}

export interface ColorRamp {
  name: string;
  description: string;
  stops: ColorStop[];
}

// Precipitation color ramps (Blues gradient)
export const PRECIP_BLUE: ColorRamp = {
  name: "Blue",
  description: "Light to dark blue gradient",
  stops: [
    { position: 0.0, color: [240, 240, 240] }, // Very light gray (dry)
    { position: 0.2, color: [210, 230, 255] }, // Light blue
    { position: 0.4, color: [160, 200, 255] }, // Medium light blue
    { position: 0.6, color: [100, 150, 240] }, // Medium blue
    { position: 0.8, color: [50, 100, 200] },  // Dark blue
    { position: 1.0, color: [10, 30, 120] },   // Very dark blue (wet)
  ],
};

export const PRECIP_VIRIDIS: ColorRamp = {
  name: "Viridis",
  description: "Perceptually uniform colormap",
  stops: [
    { position: 0.0, color: [68, 1, 84] },     // Dark purple
    { position: 0.25, color: [59, 82, 139] },  // Blue-purple
    { position: 0.5, color: [33, 145, 140] },  // Teal
    { position: 0.75, color: [94, 201, 98] },  // Green
    { position: 1.0, color: [253, 231, 37] },  // Yellow
  ],
};

// Temperature color ramps (Cool to warm)
export const TEMP_BLUE_RED: ColorRamp = {
  name: "Blue-Red",
  description: "Cold to hot gradient",
  stops: [
    { position: 0.0, color: [10, 0, 80] },     // Dark blue (very cold)
    { position: 0.2, color: [80, 120, 200] },  // Blue (cold)
    { position: 0.4, color: [130, 180, 255] }, // Light blue (cool)
    { position: 0.5, color: [180, 220, 255] }, // Very light blue (mild)
    { position: 0.6, color: [220, 240, 200] }, // Light green-white (warm)
    { position: 0.7, color: [255, 255, 180] }, // Light yellow (warm)
    { position: 0.8, color: [255, 200, 120] }, // Yellow-orange (hot)
    { position: 0.9, color: [240, 120, 60] },  // Orange (very hot)
    { position: 1.0, color: [180, 50, 30] },   // Dark red (extreme heat)
  ],
};

export const TEMP_TURBO: ColorRamp = {
  name: "Turbo",
  description: "Rainbow gradient optimized for perception",
  stops: [
    { position: 0.0, color: [48, 18, 59] },    // Dark purple
    { position: 0.2, color: [61, 82, 161] },   // Blue
    { position: 0.4, color: [33, 145, 140] },  // Cyan
    { position: 0.5, color: [94, 201, 98] },   // Green
    { position: 0.6, color: [170, 220, 50] },  // Yellow-green
    { position: 0.8, color: [253, 135, 5] },   // Orange
    { position: 1.0, color: [122, 4, 3] },     // Dark red
  ],
};

// Grayscale for debugging
export const GRAYSCALE: ColorRamp = {
  name: "Grayscale",
  description: "Simple black to white gradient",
  stops: [
    { position: 0.0, color: [0, 0, 0] },       // Black
    { position: 1.0, color: [255, 255, 255] }, // White
  ],
};

// Color ramp collections by data type
export const PRECIPITATION_RAMPS: ColorRamp[] = [
  PRECIP_BLUE,
  PRECIP_VIRIDIS,
  GRAYSCALE,
];

export const TEMPERATURE_RAMPS: ColorRamp[] = [
  TEMP_BLUE_RED,
  TEMP_TURBO,
  GRAYSCALE,
];

/**
 * Get default color ramp for a given layer type
 */
export function getDefaultColorRamp(layerType: string): ColorRamp {
  if (layerType === "precip") {
    return PRECIP_BLUE;
  }
  return TEMP_BLUE_RED;
}

/**
 * Get available color ramps for a given layer type
 */
export function getAvailableRamps(layerType: string): ColorRamp[] {
  if (layerType === "precip") {
    return PRECIPITATION_RAMPS;
  }
  return TEMPERATURE_RAMPS;
}
